import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { HardHat, PaperPlaneRight, ArrowLeft } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Public forgot-password screen. Submits the email; the backend always
 * returns 200 (to prevent user enumeration), so we show the same generic
 * success message regardless of whether the account exists.
 */
export default function ForgotPassword() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg shadow-[0_8px_32px_rgba(0,0,0,0.08)] p-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-10 w-10 rounded-md bg-blue-900 flex items-center justify-center">
            <HardHat size={22} weight="fill" className="text-amber-400" />
          </div>
          <div className="leading-tight">
            <h1 className="font-heading font-black text-lg tracking-tight text-slate-900">{t("fpTitle")}</h1>
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">UG Managment</p>
          </div>
        </div>

        {sent ? (
          <div data-testid="fp-success" className="space-y-4">
            <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900">
              <p className="font-bold mb-2 flex items-center gap-2">
                <PaperPlaneRight size={16} weight="fill" /> {t("fpSentTitle")}
              </p>
              <p>{t("fpSentBody")}</p>
              <p className="text-xs mt-2 opacity-75">{t("fpSentSpam")}</p>
            </div>
            <Button data-testid="fp-back-login" onClick={() => navigate("/login")} className="w-full bg-blue-900 hover:bg-blue-800">
              <ArrowLeft size={14} className="mr-2" /> {t("fpBackLogin")}
            </Button>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-6">{t("fpBlurb")}</p>
            <form onSubmit={submit} className="space-y-5">
              <div className="space-y-2">
                <Label className="overline-label">{t("email")}</Label>
                <Input data-testid="fp-email" type="email" required value={email}
                        onChange={(e) => setEmail(e.target.value)} placeholder="tu@correo.com" />
              </div>
              <Button data-testid="fp-submit" type="submit" disabled={busy}
                       className="w-full bg-blue-900 hover:bg-blue-800 font-semibold">
                {busy ? "..." : t("fpSubmit")}
              </Button>
            </form>
            <Link data-testid="fp-back" to="/login" className="block text-center mt-5 text-xs text-slate-500 hover:text-blue-900">
              ← {t("fpBackLogin")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
